import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, KeyRound, Shield, ShieldOff, User as UserIcon, X, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const EMPTY = { email: "", name: "", password: "", role: "user" };

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null); // user object being reset
  const [resetPwd, setResetPwd] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Palavra-passe com mínimo 8 caracteres");
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Utilizador criado");
      setOpenForm(false);
      setForm(EMPTY);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(u) {
    if (!window.confirm(`Eliminar utilizador "${u.email}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utilizador eliminado");
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  async function onResetSubmit(e) {
    e.preventDefault();
    if (!resetting) return;
    if (resetPwd.length < 8) return toast.error("Mínimo 8 caracteres");
    try {
      await api.post(`/admin/users/${resetting.id}/reset-password`, { new_password: resetPwd });
      toast.success(`Palavra-passe de ${resetting.email} atualizada`);
      setResetting(null);
      setResetPwd("");
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  async function onToggleRole(u) {
    const isAdmin = u.role === "admin";
    const nextRole = isAdmin ? "user" : "admin";
    const label = isAdmin ? "remover permissões de administrador a" : "promover a administrador";
    if (!window.confirm(`Tem a certeza que quer ${label} "${u.email}"?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: nextRole });
      toast.success(
        isAdmin ? `${u.email} deixou de ser administrador` : `${u.email} agora é administrador`
      );
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm font-manrope text-neutral-600">
          {items.length} utilizador{items.length === 1 ? "" : "es"} com acesso à plataforma.
        </div>
        <button
          data-testid="admin-user-new-btn"
          onClick={() => {
            setForm(EMPTY);
            setOpenForm(true);
          }}
          className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Novo utilizador
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">A carregar…</div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center">
            <Users className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm font-manrope text-neutral-500">Sem utilizadores.</div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Email</th>
                <th className="py-3 px-6 text-left">Nome</th>
                <th className="py-3 px-6 text-left">Papel</th>
                <th className="py-3 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold">
                    {u.email}
                    {u.id === currentUser?.id && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#0047FF] font-chivo font-bold">
                        · Você
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">{u.name}</td>
                  <td className="py-4 px-6">
                    <RoleChip role={u.role} />
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`toggle-role-${u.id}`}
                        onClick={() => onToggleRole(u)}
                        disabled={u.id === currentUser?.id}
                        className={`p-2 rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                          u.role === "admin"
                            ? "text-[#0047FF] hover:text-neutral-500 hover:bg-neutral-100"
                            : "text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5"
                        }`}
                        title={
                          u.id === currentUser?.id
                            ? "Não pode alterar o seu próprio papel"
                            : u.role === "admin"
                            ? "Remover permissões de administrador"
                            : "Promover a administrador"
                        }
                      >
                        {u.role === "admin" ? (
                          <ShieldOff className="w-4 h-4" strokeWidth={1.5} />
                        ) : (
                          <Shield className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </button>
                      <button
                        data-testid={`reset-${u.id}`}
                        onClick={() => {
                          setResetting(u);
                          setResetPwd("");
                        }}
                        className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm transition-colors"
                        title="Repor palavra-passe"
                      >
                        <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => onDelete(u)}
                        disabled={u.id === currentUser?.id}
                        className="p-2 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.id === currentUser?.id ? "Não pode eliminar a sua conta" : "Eliminar"}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <Modal onClose={() => setOpenForm(false)} title="Novo utilizador" testid="admin-user-modal">
          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-user-form">
            <FieldText label="Email *" testid="input-user-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <FieldText label="Nome *" testid="input-user-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <FieldText label="Palavra-passe (mín 8) *" testid="input-user-password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">Papel *</label>
              <select
                data-testid="input-user-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
              >
                <option value="user">Utilizador</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <FormActions onCancel={() => setOpenForm(false)} saving={saving} label="Criar utilizador" />
          </form>
        </Modal>
      )}

      {resetting && (
        <Modal onClose={() => setResetting(null)} title={`Repor palavra-passe · ${resetting.email}`} testid="admin-reset-modal">
          <form onSubmit={onResetSubmit} className="space-y-5" data-testid="admin-reset-form">
            <FieldText label="Nova palavra-passe (mín 8) *" testid="input-reset-password" type="password" value={resetPwd} onChange={setResetPwd} required />
            <FormActions onCancel={() => setResetting(null)} label="Confirmar" />
          </form>
        </Modal>
      )}
    </div>
  );
}

function RoleChip({ role }) {
  const isAdmin = role === "admin";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${
        isAdmin
          ? "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/20"
          : "bg-zinc-100 text-neutral-600 border-zinc-200"
      }`}
    >
      {isAdmin ? <Shield className="w-3 h-3" strokeWidth={2} /> : <UserIcon className="w-3 h-3" strokeWidth={2} />}
      {isAdmin ? "Administrador" : "Utilizador"}
    </span>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
          <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">{title}</h2>
          <button type="button" onClick={onClose} className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
      />
    </div>
  );
}

function FormActions({ onCancel, saving, label = "Guardar" }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
      <button type="button" data-testid="cancel-btn" onClick={onCancel} className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm">
        Cancelar
      </button>
      <button type="submit" data-testid="save-btn" disabled={saving} className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60">
        {saving ? "A guardar…" : label}
      </button>
    </div>
  );
}
